/**
 * IconButton — square icon-only button with mandatory aria-label.
 *
 * WHEN TO USE: actions where space is tight (table rows, toolbars, close buttons).
 * WHAT NOT TO DO: don't skip aria-label — screen readers need it.
 */
import React from 'react';
import { type LucideIcon } from 'lucide-react';

type Variant = 'ghost' | 'secondary' | 'danger';
type Size = 'sm' | 'md';

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  'aria-label': string;
  variant?: Variant;
  size?: Size;
}

const variantStyles: Record<Variant, string> = {
  ghost:
    'text-gray-400 hover:text-gray-100 hover:bg-gray-800 active:bg-gray-700 focus-visible:ring-gray-500/40',
  secondary:
    'text-gray-300 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 active:bg-gray-600 focus-visible:ring-gray-500/40',
  danger:
    'text-gray-400 hover:text-danger-400 hover:bg-danger-500/10 active:bg-danger-500/20 focus-visible:ring-danger-500/50',
};

const sizeStyles: Record<Size, { button: string; icon: string }> = {
  sm: { button: 'w-7 h-7', icon: 'w-3.5 h-3.5' },
  md: { button: 'w-8 h-8', icon: 'w-4 h-4' },
};

export default function IconButton({
  icon: Icon,
  variant = 'ghost',
  size = 'md',
  className = '',
  ...props
}: IconButtonProps) {
  return (
    <button
      className={[
        'inline-flex items-center justify-center rounded-button',
        'transition-all duration-fast ease-out-expo',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950',
        'disabled:opacity-50 disabled:pointer-events-none',
        variantStyles[variant],
        sizeStyles[size].button,
        className,
      ].join(' ')}
      {...props}
    >
      <Icon className={sizeStyles[size].icon} />
    </button>
  );
}
